import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import axios from "axios";
import DataTable from "react-data-table-component";
import { config } from "../../../config/envs";
import { jdsTableStyles } from "../../components/jds/tableStyles";
import { JdsPagination } from "../../components/jds/JdsPagination";
import { JdsButton } from "../../components/jds/JdsButton";
import { JdsSearchInput } from "../../components/jds/JdsSearchInput";
import { JdsTag } from "../../components/jds/JdsTag";
import { PlusIcon } from "../../components/jds/icons";
import "../../components/jds/jds.css";
import "./coupons.css";

// Ícone de lápis (contorno, mesmo traço de components/jds/icons.js).
const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);

const FilterComponent = ({ filterText, onClear, onFilter }) => (
  <div className="coupons-subheader">
    <JdsSearchInput
      id="search"
      placeholder="Filtro"
      filterText={filterText}
      onFilter={onFilter}
      onClear={onClear}
    />
    <Link to="/coupons/add">
      <JdsButton aria-label="Novo cupom">
        Novo cupom
        <PlusIcon />
      </JdsButton>
    </Link>
  </div>
);

export function Coupons() {
  const columns = [

    {
      name: "Ações",
      selector: row => row.id,
      width: "90px",
      cell: row => (
        <Link to={`/coupons/${row.id}`}>
          <JdsButton
            variant="ghost"
            color="neutral"
            size="sm"
            iconOnly
            title="Editar"
            aria-label="Editar cupom"
          >
            <EditIcon />
          </JdsButton>
        </Link>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    },
    {
      name: "Cadastrado em",
      selector: row => row.created_at,
      width: "150px",
      cell: row => (
        <div>
          {row.created_at
            ? format(new Date(row.created_at), "dd/MM/yyyy HH:mm")
            : "Sem data de cadastro"}
        </div>
      ),
      sortable: true
    },
    {
      name: "Nome",
      selector: row => row.coupom,
      maxWidth: "180px"
    },
    {
      name: "Status",
      selector: row => (row.active ? "Ativo" : "Inativo"),
      cell: row => (
        <JdsTag
          label={row.active ? "Ativo" : "Inativo"}
          color={row.active ? "success" : "neutral"}
          dot
        />
      ),
      width: "120px"
    },
    {
      name: "Dias de trial",
      selector: row => `${row.trial_days} dias`,
      width: "100px"
    },
    {
      name: "Duração",
      selector: row => `${row.periodicity} dias`,
      width: "100px"
    },
    {
      name: "Grupo de planos",
      selector: row => row.feature_group_name,
      width: "300px",
      cell: row => (
        <div title={`${row.feature_group_name}`}>{row.feature_group_name}</div>
      )
    },
    {
      name: "Valor",
      selector: row => `R$${row.old_amount}`,
      width: "100px"
    },
    {
      name: "Valor com desconto",
      selector: row => `R$${row.amount}`,
      width: "160px"
    }
  ];

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [filterText, setFilterText] = React.useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(
    false
  );

  const fetchCoupons = async page => {
    setLoading(true);
    const response = await axios.get(
      config.app.adminBaseUrl +
        `/coupons/?page=${page}&per_page=${perPage}&filter=${filterText}`
    );

    setData(response.data.data.data);
    setTotalRows(response.data.data.totalItems);
    setLoading(false);
  };

  const handlePageChange = page => {
    fetchCoupons(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setLoading(true);

    const response = await axios.get(
      config.app.adminBaseUrl +
        `/coupons/?page=${page}&per_page=${newPerPage}&filter=${filterText}`
    );

    setData(response.data.data.data);
    setPerPage(newPerPage);
    setLoading(false);
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText("");
      }
    };

    return (
      <FilterComponent
        onFilter={e => setFilterText(e.target.value)}
        onClear={handleClear}
        filterText={filterText}
      />
    );
  }, [filterText, resetPaginationToggle]);

  useEffect(() => {
    fetchCoupons(1);
  }, []);

  useEffect(() => {
    fetchCoupons(1);
  }, [filterText]);

  return (
    <div className="jds-scope">
      <h1 className="jds-title-lg">Cupom</h1>
      <p className="text-muted mb-10">Listagem de todos os cupons</p>

      <div className="jds-card jds-card--flush">
        <div style={{ overflowX: "auto", width: "100%" }}>
          <DataTable
            columns={columns}
            data={data}
            customStyles={jdsTableStyles}
            progressPending={loading}
            pagination
            perPage={perPage}
            paginationServer
            paginationTotalRows={totalRows}
            paginationResetDefaultPage={resetPaginationToggle} // optionally, a hook to reset pagination to page 1
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            subHeader
            subHeaderAlign="left"
            subHeaderComponent={subHeaderComponentMemo}
            paginationComponent={JdsPagination}
            responsive={false}
            style={{ width: "100%" }}
          />
        </div>
      </div>
    </div>
  );
}
